/**
 * Locale Detection — OS-level locale and country detection.
 *
 * Separated from locale.ts to avoid circular dependency with countryConfig.
 * Pure functions with no store dependency.
 */

import * as Localization from 'expo-localization';
import { getCountryConfig } from '../data/countryConfig';
import { validateSourceLocale } from './locale';
import { getCountriesData, getCourses, fallbackSourceLocale } from '../store/courseStore';

/**
 * Detect source locale from OS locale.
 * Maps the OS region code to a source locale variant via countryConfig.
 *
 * Always returns a concrete locale: unknown regions coerce to the
 * flagship-course fallback. This makes it suitable only as type-safety
 * scaffolding (e.g. seeding store state before onboarding) — user-facing
 * suggestions should use `suggestSourceLocaleFromOSLanguage()`, which
 * represents "unknown" explicitly.
 */
export function detectSourceLocale(): string {
  try {
    const locales = Localization.getLocales();
    if (locales.length > 0 && locales[0].regionCode) {
      const config = getCountryConfig(locales[0].regionCode, getCountriesData());
      return validateSourceLocale(config?.locales?.length ? config.locales[0] : undefined);
    }
  } catch {
    // Fall through to fallback
  }
  return fallbackSourceLocale();
}

/**
 * Suggest a source locale from the OS *language* (not region).
 * Returns the matching course's default source locale, or undefined when
 * the OS language doesn't match any course — the caller decides what an
 * explicit "unknown" means (e.g. onboarding preselects nothing).
 */
export function suggestSourceLocaleFromOSLanguage(): string | undefined {
  try {
    const os = Localization.getLocales()[0];
    if (!os?.languageCode) return undefined;
    const course = getCourses().find((c) => c.sourceBase === os.languageCode);
    return course?.defaultSourceLocale;
  } catch {
    return undefined;
  }
}

/**
 * Detect country code from OS locale (ISO 3166-1 alpha-2).
 */
export function detectCountry(): string | undefined {
  try {
    const locales = Localization.getLocales();
    if (locales.length > 0 && locales[0].regionCode) {
      return locales[0].regionCode;
    }
  } catch {
    // Fall through
  }
  return undefined;
}
